import { useEffect, useMemo, useState } from 'react'
import { getCachedChatUserId } from '../../../services/chat-service'
import type { ChatUser } from '../../../types/chat'
import { filterCallableContacts } from '../../../utils/bruno-config'
import { usePresenceHidden, useResolvedPresence } from '../../../stores/presence-store'
import { loadDirectory, useDirectory } from '../../../stores/directory-store'
import type { CallTransferMode } from '../../../types/voice-call'
import { evaluateTransferEligibility } from '@shared/calls/transfer-eligibility'
import { getTransferEligibilityConfig } from '../data/transfer-config'
import { buildTransferTargetContext } from '../utils/transfer-target-context'

function CloseIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function TargetRow({
  user,
  selected,
  onSelect,
}: {
  user: ChatUser
  selected: boolean
  onSelect: () => void
}) {
  const resolved = useResolvedPresence(user)
  // Presence you may not see is unknown: never warn (or block) on it as "offline".
  const presence = usePresenceHidden(user.userId) ? null : resolved
  const config = getTransferEligibilityConfig()
  const eligibility = useMemo(() => {
    const ctx = buildTransferTargetContext({ ...user, presenceStatus: presence })
    return evaluateTransferEligibility(ctx, config)
  }, [user, presence, config])

  const busyHint =
    eligibility.issues.length > 0
      ? eligibility.issues.some((i) => i.severity === 'block')
        ? 'May be unavailable'
        : 'Busy — you will be warned'
      : null

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
          selected
            ? 'border-brand-300 bg-brand-50 ring-1 ring-brand-200'
            : 'border-slate-200 hover:border-brand-200 hover:bg-slate-50'
        }`}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">{user.displayName}</p>
          <p className="truncate text-xs text-slate-500">{user.email}</p>
        </div>
        {busyHint && (
          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
            {busyHint}
          </span>
        )}
      </button>
    </li>
  )
}

export function TransferSheet({
  open,
  onClose,
  onConfirm,
  excludeUserIds = [],
}: {
  open: boolean
  onClose: () => void
  onConfirm: (target: ChatUser, mode: CallTransferMode) => void
  excludeUserIds?: string[]
}) {
  const { contacts, loading, error, loadedAt, reload } = useDirectory()
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Transfers are always BLIND: the caller is forwarded directly to the target
  // and this device drops off (no 3-way consult / group bridge).
  const mode: CallTransferMode = 'blind'

  // Refresh local UI state and ensure the cached directory is loaded once per
  // open. Keyed on `open` only — the `excludeUserIds` array prop is recreated
  // every render, so depending on it here caused the request storm.
  useEffect(() => {
    if (!open) return
    setSelectedId(null)
    setSearch('')
    void loadDirectory()
  }, [open])

  // Build a stable primitive key so the filtered list memo doesn't recompute on
  // every render just because a new `excludeUserIds` array reference came in.
  const excludeKey = Array.from(
    new Set([getCachedChatUserId() ?? '', ...excludeUserIds].filter(Boolean)),
  )
    .sort()
    .join(',')

  const directory = useMemo(() => {
    const exclude = new Set(excludeKey ? excludeKey.split(',') : [])
    return filterCallableContacts(contacts.filter((u) => !exclude.has(u.userId)))
  }, [contacts, excludeKey])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return directory
    return directory.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    )
  }, [directory, search])

  const selected = directory.find((u) => u.userId === selectedId) ?? null

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-sheet-title"
    >
      <div className="native-safe-bottom flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="transfer-sheet-title" className="text-base font-semibold text-slate-900">
            Transfer call
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close transfer sheet"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="border-b border-slate-100 px-4 py-3">
          <p className="text-xs text-slate-500">
            The caller is connected directly to the person you choose, and your
            line drops off once the transfer is sent.
          </p>
        </div>

        <div className="px-4 py-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search people…"
            aria-label="Search transfer targets"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-brand-500 focus:ring-2"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {loading || (loadedAt == null && !error) ? (
            <p className="px-2 py-8 text-center text-sm text-slate-500">Loading contacts…</p>
          ) : error ? (
            <div className="px-2 py-6 text-center">
              <p className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="mt-3 text-sm font-semibold text-brand-600 hover:underline"
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-slate-500">No contacts available.</p>
          ) : (
            <ul className="space-y-1" role="list">
              {filtered.map((user) => (
                <TargetRow
                  key={user.userId}
                  user={user}
                  selected={selectedId === user.userId}
                  onSelect={() => setSelectedId(user.userId)}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-200 p-4">
          <button
            type="button"
            disabled={!selected}
            onClick={() => selected && onConfirm(selected, mode)}
            className="a11y-touch-target w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {selected ? `Transfer to ${selected.displayName}` : 'Select someone to transfer to'}
          </button>
        </div>
      </div>
    </div>
  )
}
